// This is a TypeScript "triple-slash" directive. It tells the compiler to load the
// Google Maps type definitions so we can use "google.maps" types throughout this file.
/// <reference types="google.maps" />
import { ClientLoggingService } from './client-logging.service';

import { Injectable } from '@angular/core';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { environment } from '../../generated.environments/environment';

/**
 * Service for managing Google Maps API interactions.
 *
 * Issue #236: Google Maps Integration for Project Address
 *
 * Responsibilities:
 * - Load Google Maps JavaScript API
 * - Create and configure Places Autocomplete instances
 * - Provide geocoding utilities
 * - Handle API errors gracefully
 *
 * Architecture:
 * - Client-side browser API (not server-side)
 * - API key embedded in frontend bundle
 * - Security via domain referrer restrictions
 *
 * See: docs/05-tdd/google-maps-integration.md
 */
/**
 * Autocomplete result containing place data, compatible with legacy Autocomplete.getPlace().
 * Issue #1178: Used by createPlaceAutocomplete to provide a consistent interface.
 */
export interface PlaceResult {
  address_components?: google.maps.GeocoderAddressComponent[];
  formatted_address?: string;
  geometry?: { location: google.maps.LatLng };
  place_id?: string;
}

/**
 * Autocomplete suggestion returned by fetchAddressSuggestions().
 * Issue #1178: Used with mat-autocomplete for programmatic address lookup.
 */
export interface AddressSuggestion {
  /** Display text for the suggestion */
  text: string;
  /** Google Place ID */
  placeId: string;
  /** Raw placePrediction for fetching full details via getPlaceDetails() */
  placePrediction: any;
}

/** Service for loading the Google Maps API and providing autocomplete and geocoding helpers. */
@Injectable({
  providedIn: 'root',
})
export class GoogleMapsService {
  private isLoaded = false;

  constructor() {
    // Set global options for the API
    setOptions({
      key: environment.googleMapsApiKey,
      v: 'weekly',
      region: 'US', // Optimize for US addresses
      language: 'en', // English language
      libraries: ['places', 'marker'],
    });
  }

  /**
   * Loads the Google Maps JavaScript API.
   * Called automatically by other methods, but can be called explicitly for preloading.
   */
  async loadGoogleMaps(): Promise<void> {
    if (this.isLoaded) {
      return;
    }

    try {
      // Import required libraries
      await importLibrary('places');
      await importLibrary('marker');
      await importLibrary('maps');
      this.isLoaded = true;
      ClientLoggingService.log('INFO', '[GoogleMapsService] API loaded successfully');
    } catch (error) {
      ClientLoggingService.log('ERROR', '[GoogleMapsService] Failed to load API:', error);
      throw new Error('Failed to load Google Maps API');
    }
  }

  /**
   * Session token for billing-efficient autocomplete.
   * A session groups the autocomplete search + place details fetch into one billing unit.
   */
  private sessionToken: google.maps.places.AutocompleteSessionToken | null = null;

  /**
   * Fetches address autocomplete suggestions using the new programmatic API.
   *
   * Migration from deprecated `google.maps.places.Autocomplete` widget (Issue #1178).
   * Uses `AutocompleteSuggestion.fetchAutocompleteSuggestions()` which is not deprecated
   * and works with any UI (e.g., mat-autocomplete).
   *
   * @param input - User's typed input string
   * @param countryCode - ISO 3166-1 Alpha-2 country code (default: 'us')
   * @returns Array of address suggestions with text and placeId
   */
  async fetchAddressSuggestions(input: string, countryCode: string = 'us'): Promise<AddressSuggestion[]> {
    if (!input || input.length < 3) {
      return [];
    }

    // Normalize to 2-letter CLDR region code (e.g., 'USA' → 'us', 'US' → 'us')
    const normalizedCountry =
      countryCode.length === 3 ? countryCode.substring(0, 2).toLowerCase() : countryCode.toLowerCase();

    await this.loadGoogleMaps();

    // Create a new session token if one doesn't exist
    if (!this.sessionToken) {
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();
    }

    try {
      const request = {
        input,
        sessionToken: this.sessionToken,
        includedRegionCodes: [normalizedCountry],
        includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route'],
        language: 'en-US',
      };

      const { suggestions } = await (google.maps.places as any).AutocompleteSuggestion.fetchAutocompleteSuggestions(
        request
      );

      return suggestions.map((s: any) => ({
        text: s.placePrediction?.text?.text || s.placePrediction?.toString() || '',
        placeId: s.placePrediction?.placeId || '',
        placePrediction: s.placePrediction,
      }));
    } catch (error) {
      ClientLoggingService.log('ERROR', '[GoogleMapsService] Failed to fetch suggestions:', error);
      return [];
    }
  }

  /**
   * Fetches full place details from a suggestion's placePrediction.
   * Resets the session token after fetching (completes the billing session).
   *
   * @param placePrediction - The placePrediction object from a suggestion
   * @returns PlaceResult with address components, geometry, etc.
   */
  async getPlaceDetails(placePrediction: any): Promise<PlaceResult | null> {
    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'location', 'displayName'],
      });

      // Reset session token after place details fetch (ends billing session)
      this.sessionToken = null;

      const result: PlaceResult = {
        formatted_address: place.formattedAddress || undefined,
        place_id: place.id || undefined,
        geometry: place.location ? { location: place.location as google.maps.LatLng } : undefined,
      };

      // Map addressComponents to legacy address_components format
      if (place.addressComponents) {
        result.address_components = place.addressComponents.map((c: any) => ({
          long_name: c.longText || c.long_name || '',
          short_name: c.shortText || c.short_name || '',
          types: c.types || [],
        }));
      }

      ClientLoggingService.log('INFO', '[GoogleMapsService] Place details fetched:', result);
      return result;
    } catch (error) {
      ClientLoggingService.log('ERROR', '[GoogleMapsService] Failed to fetch place details:', error);
      this.sessionToken = null; // Reset even on error
      return null;
    }
  }

  /**
   * Geocodes an address string to lat/lng coordinates.
   *
   * @param address - Full address string to geocode
   * @param timeoutMs - Maximum milliseconds to wait for the geocoder callback (default: 5000ms)
   * @returns Geocoding result with coordinates, or null if failed / timed out
   */
  async geocodeAddress(address: string, timeoutMs = 5000): Promise<google.maps.GeocoderResult | null> {
    await this.loadGoogleMaps();

    const geocoder = new google.maps.Geocoder();

    return new Promise(resolve => {
      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          ClientLoggingService.log('WARN', `[GoogleMapsService] Geocoding timed out after ${timeoutMs}ms`);
          resolve(null);
        }
      }, timeoutMs);

      try {
        geocoder.geocode(
          {
            address,
            region: 'us', // Bias to US addresses
          },
          (results, status) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);

            if (status === 'OK' && results && results.length > 0) {
              ClientLoggingService.log('INFO', '[GoogleMapsService] Geocoding successful:', results[0]);
              resolve(results[0]);
            } else if (status === 'ZERO_RESULTS') {
              // Valid response, just no matching address found
              ClientLoggingService.log('DEBUG', '[GoogleMapsService] Geocoding returned no results for address');
              resolve(null);
            } else {
              // Actual errors: OVER_QUERY_LIMIT, REQUEST_DENIED, INVALID_REQUEST, UNKNOWN_ERROR
              ClientLoggingService.log('ERROR', '[GoogleMapsService] Geocoding error:', status);
              resolve(null);
            }
          }
        );
      } catch (err) {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          ClientLoggingService.log('ERROR', '[GoogleMapsService] Geocoding threw exception:', err);
          resolve(null);
        }
      }
    });
  }

  /**
   * Reverse geocodes coordinates to an address.
   * Useful for validating or updating existing lat/lng.
   *
   * @param lat - Latitude
   * @param lng - Longitude
   * @param timeoutMs - Maximum milliseconds to wait for the geocoder callback (default: 5000ms)
   * @returns Reverse geocoding result, or null if failed / timed out
   */
  async reverseGeocode(lat: number, lng: number, timeoutMs = 5000): Promise<google.maps.GeocoderResult | null> {
    await this.loadGoogleMaps();

    const geocoder = new google.maps.Geocoder();

    return new Promise(resolve => {
      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          ClientLoggingService.log(
            'WARN',
            `[GoogleMapsService] Reverse geocoding timed out after ${timeoutMs}ms for (${lat}, ${lng})`
          );
          resolve(null);
        }
      }, timeoutMs);

      try {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);

          if (status === 'OK' && results && results.length > 0) {
            resolve(results[0]);
          } else if (status === 'ZERO_RESULTS') {
            // Valid response, just no matching address found for these coordinates
            ClientLoggingService.log('DEBUG', '[GoogleMapsService] Reverse geocoding returned no results');
            resolve(null);
          } else {
            // Actual errors: OVER_QUERY_LIMIT, REQUEST_DENIED, INVALID_REQUEST, UNKNOWN_ERROR
            ClientLoggingService.log('ERROR', '[GoogleMapsService] Reverse geocoding error:', status);
            resolve(null);
          }
        });
      } catch (err) {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          ClientLoggingService.log('ERROR', '[GoogleMapsService] Reverse geocoding threw exception:', err);
          resolve(null);
        }
      }
    });
  }

  /**
   * Checks if the API is loaded and functional.
   */
  isApiLoaded(): boolean {
    return this.isLoaded && typeof google !== 'undefined';
  }
}
